import { useState } from "react";
import { Send, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const FEEDBACK_EMAIL = "michaelmascolo@commongroundinstitute.net";

export default function FeedbackForm({ onSubmitted }) {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please write a few words first.");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        email: email.trim(),
        page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      setSent(true);
      toast.success("Thank you — your feedback means a lot!");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="feedback-thanks">
        <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-[#4A5D4E]/10 flex items-center justify-center">
          <Check className="h-6 w-6 text-[#4A5D4E]" strokeWidth={2} />
        </div>
        <p className="font-serif text-xl text-[#2D2A26] mb-1">Thank you!</p>
        <p className="text-sm text-[#6E6860]">
          Your thoughts help us make Common Ground Explorer better for everyone.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
      <div>
        <label className="block text-sm font-medium text-[#2D2A26] mb-1.5">
          What's working, what's confusing, what would you change?
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          maxLength={5000}
          placeholder="Share anything — big ideas or tiny nitpicks. We read every note."
          data-testid="feedback-message-input"
          className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 text-sm text-[#2D2A26] placeholder:text-[#A69F94] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/40 resize-none"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#2D2A26] mb-1.5">
          Your email <span className="text-[#A69F94] font-normal">(optional — if you'd like a reply)</span>
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="feedback-email-input"
          className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-2.5 text-sm text-[#2D2A26] placeholder:text-[#A69F94] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/40"
        />
      </div>
      <div className="flex flex-wrap items-center gap-4 pt-1">
        <button
          type="submit"
          disabled={sending}
          data-testid="feedback-submit-button"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 shadow-sm disabled:opacity-60"
        >
          <Send className="h-4 w-4" strokeWidth={1.75} /> {sending ? "Sending…" : "Send feedback"}
        </button>
        <a
          href={`mailto:${FEEDBACK_EMAIL}?subject=Common%20Ground%20Explorer%20feedback`}
          data-testid="feedback-mailto-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A5D4E] hover:text-[#3B4A3E] underline underline-offset-4"
        >
          <Mail className="h-4 w-4" strokeWidth={1.75} /> or email us directly
        </a>
      </div>
    </form>
  );
}
